import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Autocomplete,
  Tooltip,
  IconButton,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { toast } from '../toast/modernToast';
import { payrollValidationSchema } from '../../utils/payrollValidation';
import {
  MONTH_CHOICES,
  YEAR_CHOICES,
  PAYMENT_STATUS_CHOICES,
  PAYMENT_MODE_CHOICES,
  PAYMENT_CURRENCY_CHOICES,
  getPayrollFormDataFromRecord,
  formatPayrollDataForSubmission,
  calculateGrossPayable,
  calculateNetPayable,
} from '../../utils/payrollUtils';
import { getPayrolls, updatePayroll, getBatchEditPayrollFilters } from '../../services/api';

const getStatusColor = (status) => {
  const map = {
    PENDING: 'warning',
    APPROVED: 'info',
    PAID: 'success',
    'ON HOLD': 'default',
    REJECTED: 'error',
  };
  return map[String(status || '').toUpperCase()] || 'default';
};

const getInitials = (name = '') =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

function BatchEditPayrollDialog({ open, onClose, onPayrollUpdated }) {
  const getPreviousPayrollPeriod = () => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      month: d.toLocaleString('default', { month: 'long' }),
      year: now.getFullYear(),
    };
  };

  const prev = getPreviousPayrollPeriod();

  // ── Filter options fetched from backend ───────────────────────────────────
  const ALL_DEPARTMENTS = 'All Departments';
  const [filterOptions, setFilterOptions] = useState({ months: [], years: [], departments: [] });

  // ── Dialog-level filters ──────────────────────────────────────────────────
  const [filters, setFilters] = useState({ month: prev.month, year: prev.year, department: '' });

  // ── Left panel state ──────────────────────────────────────────────────────
  const [payrolls, setPayrolls] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoadingList, setIsLoadingList] = useState(false);

  // ── Right panel state ─────────────────────────────────────────────────────
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // ── Fetch filter options once on open ─────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    getBatchEditPayrollFilters()
      .then((res) => setFilterOptions(res.data))
      .catch((err) => console.error('Error loading batch edit filter options:', err));
  }, [open]);

  // ── Fetch payrolls from backend (real pagination + all filters) ───────────
  const loadPayrolls = useCallback(async () => {
    if (!filters.month || !filters.year) return;
    setIsLoadingList(true);
    setSelectedPayroll(null);
    setFormData({});
    setFormErrors({});
    try {
      const dept = filters.department === ALL_DEPARTMENTS ? '' : filters.department;
      const response = await getPayrolls(page, pageSize, '', '', filters.month, filters.year, '', '', dept);
      const { data } = response;
      setPayrolls(data.results || []);
      setTotalCount(data.pagination_info?.count ?? data.results?.length ?? 0);
      setTotalPages(data.pagination_info?.total_pages ?? 1);
    } catch (error) {
      console.error('Error loading payrolls for batch edit:', error);
      setPayrolls([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setIsLoadingList(false);
    }
  }, [filters, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when dialog opens or filters/page change
  useEffect(() => {
    if (open) loadPayrolls();
  }, [open, loadPayrolls]);

  // Reset to page 1 when filters change
  const handleFilterChange = (field, value) => {
    setFilters((f) => ({ ...f, [field]: value }));
    setPage(1);
  };

  // ── Select a payroll record to edit ──────────────────────────────────────
  const handleSelectPayroll = (payroll) => {
    setSelectedPayroll(payroll);
    setFormData(getPayrollFormDataFromRecord(payroll));
    setFormErrors({});
  };

  // ── Form field changes ────────────────────────────────────────────────────
  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (
        [
          'basic_salary',
          'transport_allowance',
          'bonus_dues',
          'weekends_pay',
          'absent_deductions',
          'loan_deductions',
          'tax_deductions',
          'other_deductions',
          'late_deductions',
        ].includes(field)
      ) {
        next.gross_payable = calculateGrossPayable(next);
        next.net_payable = calculateNetPayable(next);
      }
      return next;
    });
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // ── Save (returns true on success) ───────────────────────────────────────
  const handleSave = async () => {
    try {
      await payrollValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
    } catch (validationError) {
      if (validationError.name === 'ValidationError') {
        const errors = {};
        validationError.inner.forEach((e) => {
          errors[e.path] = e.message;
        });
        setFormErrors(errors);
        return false;
      }
    }

    try {
      setIsSaving(true);
      const submitData = formatPayrollDataForSubmission(formData);
      const updated = await updatePayroll(selectedPayroll.id, submitData);

      // Refresh the record in the current page list
      const updatedPayroll = updated.data?.payroll || { ...selectedPayroll, ...submitData };
      setPayrolls((prev) => prev.map((p) => (p.id === selectedPayroll.id ? { ...p, ...updatedPayroll } : p)));
      setSelectedPayroll((prev) => ({ ...prev, ...updatedPayroll }));

      toast.success('Payroll record updated successfully');
      if (onPayrollUpdated) onPayrollUpdated();
      return true;
    } catch (error) {
      console.error('Error saving payroll:', error);
      toast.error('Failed to save payroll record');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // ── Save & move to next record ────────────────────────────────────────────
  const handleSaveAndNext = async () => {
    const saved = await handleSave();
    if (!saved) return;

    const currentIndex = payrolls.findIndex((p) => p.id === selectedPayroll.id);
    const nextInPage = payrolls[currentIndex + 1];

    if (nextInPage) {
      handleSelectPayroll(nextInPage);
    } else if (page < totalPages) {
      // Move to next page; loadPayrolls will fire and clear selectedPayroll —
      // so just advance the page; the user can pick the first record there.
      setPage((p) => p + 1);
    } else {
      toast.info('No more payroll records.');
    }
  };

  // ── Close & reset ─────────────────────────────────────────────────────────
  const handleClose = () => {
    setSelectedPayroll(null);
    setFormData({});
    setFormErrors({});
    onClose();
  };

  const monthOptions = filterOptions.months?.length ? filterOptions.months : MONTH_CHOICES;
  const yearOptions = filterOptions.years?.length ? filterOptions.years : YEAR_CHOICES;
  const departmentOptions = [
    ALL_DEPARTMENTS,
    ...(filterOptions.departments?.length
      ? filterOptions.departments
      : Array.from(new Set(payrolls.map((p) => p.employee?.department).filter(Boolean))).sort()),
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Batch Edit Payroll</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── Filters ── */}
        <Box display="flex" gap={2} mt={2} flexWrap="wrap" alignItems="center">
          <Autocomplete
            options={monthOptions}
            value={filters.month || null}
            onChange={(_, v) => handleFilterChange('month', v || '')}
            renderInput={(params) => <TextField {...params} label="Month" size="small" />}
            size="small"
            sx={{ width: 180 }}
            disableClearable={false}
            isOptionEqualToValue={(o, v) => o === v}
          />
          <Autocomplete
            options={yearOptions}
            value={filters.year || null}
            onChange={(_, v) => handleFilterChange('year', v || '')}
            renderInput={(params) => <TextField {...params} label="Year" size="small" />}
            size="small"
            sx={{ width: 150 }}
            disableClearable={false}
            isOptionEqualToValue={(o, v) => o === v}
          />
          <Autocomplete
            options={departmentOptions}
            value={filters.department || ALL_DEPARTMENTS}
            onChange={(_, v) => handleFilterChange('department', v === ALL_DEPARTMENTS ? '' : v || '')}
            renderInput={(params) => <TextField {...params} label="Department" size="small" />}
            size="small"
            sx={{ width: 220 }}
            disableClearable
            isOptionEqualToValue={(o, v) => o === v}
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        {/* ── Left panel: employee list ── */}
        <Box
          sx={{
            width: 300,
            minWidth: 280,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary">
              {totalCount} payroll record{totalCount !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {isLoadingList ? (
            <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
              <CircularProgress />
            </Box>
          ) : payrolls.length === 0 ? (
            <Box display="flex" alignItems="center" justifyContent="center" flex={1} p={2}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No payroll records found for the selected filters.
              </Typography>
            </Box>
          ) : (
            <>
              <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
                {payrolls.map((payroll) => {
                  const isSelected = selectedPayroll?.id === payroll.id;
                  const name = payroll.employee?.full_name || 'Unknown';
                  const empId = payroll.employee?.employee_id || '';
                  return (
                    <React.Fragment key={payroll.id}>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleSelectPayroll(payroll)}
                        sx={{
                          py: 1.5,
                          '&.Mui-selected': {
                            bgcolor: 'primary.lighter',
                            '&:hover': { bgcolor: 'primary.lighter' },
                          },
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: isSelected ? 'primary.main' : 'grey.400',
                              fontSize: '0.75rem',
                            }}
                          >
                            {getInitials(name)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={isSelected ? 600 : 400} noWrap>
                              {name}
                            </Typography>
                          }
                          secondary={
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                              <Typography variant="caption" color="text.secondary">
                                {empId}
                              </Typography>
                              <Chip
                                label={payroll.payment_status}
                                color={getStatusColor(payroll.payment_status)}
                                size="small"
                                sx={{ height: 16, fontSize: '0.6rem' }}
                              />
                            </Box>
                          }
                        />
                        {isSelected && (
                          <Tooltip title="Currently editing">
                            <EditIcon fontSize="small" color="primary" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </ListItemButton>
                      <Divider component="li" />
                    </React.Fragment>
                  );
                })}
              </List>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="nowrap"
                gap={1}
                py={1}
                px={2}
                borderTop={1}
                borderColor="divider"
                sx={{ minWidth: 0 }}
              >
                <Box component="span" display="inline-flex" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
                  <Typography
                    component="label"
                    variant="caption"
                    color="text.secondary"
                    htmlFor="batch-edit-payroll-page-size"
                    sx={{ whiteSpace: 'nowrap', cursor: 'default' }}
                  >
                    Per page
                  </Typography>
                  <Select
                    id="batch-edit-payroll-page-size"
                    size="small"
                    variant="outlined"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    inputProps={{ 'aria-label': 'Rows per page' }}
                    MenuProps={{ MenuListProps: { dense: true } }}
                    sx={{
                      width: 52,
                      maxWidth: 52,
                      height: 32,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                      },
                      '& .MuiSelect-select': {
                        py: 0.25,
                        pr: 2.75,
                        pl: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: 0,
                        fontSize: '0.8125rem',
                        fontVariantNumeric: 'tabular-nums',
                      },
                    }}
                  >
                    {[5, 10, 15, 25].map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    flexShrink: 0,
                    height: 32,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    px: 0.125,
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="Previous page"
                    disabled={page <= 1 || totalPages <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    sx={{ p: 0.35, color: 'primary.main' }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Typography
                    variant="caption"
                    component="span"
                    sx={{
                      px: 0.5,
                      minWidth: 40,
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      lineHeight: 1,
                      color: 'text.primary',
                    }}
                  >
                    {totalPages > 0 ? `${page}/${totalPages}` : '—'}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Next page"
                    disabled={page >= totalPages || totalPages <= 1}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    sx={{ p: 0.35, color: 'primary.main' }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* ── Right panel: edit form ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          {!selectedPayroll ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              gap={2}
              color="text.secondary"
            >
              <EditIcon sx={{ fontSize: 56, opacity: 0.25 }} />
              <Typography variant="body1" color="text.secondary">
                Select an employee from the list to edit their payroll record.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Header */}
              <Box mb={3}>
                <Typography variant="h6">{selectedPayroll.employee?.full_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPayroll.employee?.employee_id}
                  {selectedPayroll.employee?.department ? ` · ${selectedPayroll.employee.department}` : ''}
                  {' · '}
                  {selectedPayroll.month} {selectedPayroll.year}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {/* ── Payment Information ── */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" fontWeight={600} gutterBottom>
                    Payment Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth error={!!formErrors.payment_status}>
                        <InputLabel>Payment Status</InputLabel>
                        <Select
                          value={formData.payment_status || ''}
                          label="Payment Status"
                          onChange={(e) => handleInputChange('payment_status', e.target.value)}
                        >
                          {PAYMENT_STATUS_CHOICES.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                              {s.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.payment_status && (
                          <Typography variant="caption" color="error">
                            {formErrors.payment_status}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth error={!!formErrors.payment_currency}>
                        <InputLabel>Payment Currency</InputLabel>
                        <Select
                          value={formData.payment_currency || ''}
                          label="Payment Currency"
                          onChange={(e) => handleInputChange('payment_currency', e.target.value)}
                        >
                          {PAYMENT_CURRENCY_CHOICES.map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.payment_currency && (
                          <Typography variant="caption" color="error">
                            {formErrors.payment_currency}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth error={!!formErrors.payment_mode}>
                        <InputLabel>Payment Mode</InputLabel>
                        <Select
                          value={formData.payment_mode || ''}
                          label="Payment Mode"
                          onChange={(e) => handleInputChange('payment_mode', e.target.value)}
                        >
                          {PAYMENT_MODE_CHOICES.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.payment_mode && (
                          <Typography variant="caption" color="error">
                            {formErrors.payment_mode}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Payment Date"
                          value={formData.payment_date ? dayjs(formData.payment_date) : null}
                          onChange={(v) => handleInputChange('payment_date', v ? v.format('YYYY-MM-DD') : '')}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!formErrors.payment_date,
                              helperText: formErrors.payment_date,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </Grid>

                {/* ── Salary & Allowances ── */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" fontWeight={600} gutterBottom>
                    Salary &amp; Allowances
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Basic Salary *"
                        type="number"
                        value={formData.basic_salary ?? ''}
                        onChange={(e) =>
                          handleInputChange('basic_salary', Math.max(0, parseInt(e.target.value, 10) || 0))
                        }
                        inputProps={{ min: 0, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.basic_salary}
                        helperText={formErrors.basic_salary}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Transport Allowance"
                        type="number"
                        value={formData.transport_allowance ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'transport_allowance',
                            e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                          )
                        }
                        inputProps={{ min: 0, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.transport_allowance}
                        helperText={formErrors.transport_allowance}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Bonus Dues"
                        type="number"
                        value={formData.bonus_dues ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'bonus_dues',
                            e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                          )
                        }
                        inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.bonus_dues}
                        helperText={formErrors.bonus_dues}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Weekends Pay"
                        type="number"
                        value={formData.weekends_pay ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'weekends_pay',
                            e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                          )
                        }
                        inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.weekends_pay}
                        helperText={formErrors.weekends_pay}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Worked Days *"
                        type="number"
                        value={formData.worked_days ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'worked_days',
                            e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value) || 0)
                          )
                        }
                        inputProps={{ min: 0, step: 0.1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.worked_days}
                        helperText={formErrors.worked_days}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Gross Payable"
                        type="number"
                        value={formData.gross_payable ?? ''}
                        disabled
                        sx={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: 'rgba(0,0,0,0.87)',
                            fontWeight: 'bold',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* ── Deductions ── */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" fontWeight={600} gutterBottom>
                    Deductions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Absent Deductions"
                        type="number"
                        value={formData.absent_deductions ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'absent_deductions',
                            e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                          )
                        }
                        inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.absent_deductions}
                        helperText={formErrors.absent_deductions}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Loan Deductions"
                        type="number"
                        value={formData.loan_deductions ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'loan_deductions',
                            e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                          )
                        }
                        inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.loan_deductions}
                        helperText={formErrors.loan_deductions}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Tax Deductions"
                        type="number"
                        value={formData.tax_deductions ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'tax_deductions',
                            e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                          )
                        }
                        inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.tax_deductions}
                        helperText={formErrors.tax_deductions}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Other Deductions"
                        type="number"
                        value={formData.other_deductions ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'other_deductions',
                            e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                          )
                        }
                        inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.other_deductions}
                        helperText={formErrors.other_deductions}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Late Deductions"
                        type="number"
                        value={formData.late_deductions ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'late_deductions',
                            e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                          )
                        }
                        inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.late_deductions}
                        helperText={formErrors.late_deductions}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Absent Days"
                        type="number"
                        value={formData.absent_days ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'absent_days',
                            e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value))
                          )
                        }
                        inputProps={{ min: 0, step: 0.1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.absent_days}
                        helperText={formErrors.absent_days}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Leave Days"
                        type="number"
                        value={formData.leave_days ?? ''}
                        onChange={(e) =>
                          handleInputChange(
                            'leave_days',
                            e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value))
                          )
                        }
                        inputProps={{ min: 0, step: 0.1, onFocus: (e) => e.target.select() }}
                        error={!!formErrors.leave_days}
                        helperText={formErrors.leave_days}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Net Payable"
                        type="number"
                        value={formData.net_payable ?? ''}
                        disabled
                        sx={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: 'rgba(0,0,0,0.87)',
                            fontWeight: 'bold',
                            fontSize: '1.05rem',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2 }}>
        {selectedPayroll && (
          <>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAndNext}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSaving ? 'Saving…' : 'Save and Next'}
            </Button>
          </>
        )}
        <Button onClick={handleClose} color="error" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BatchEditPayrollDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPayrollUpdated: PropTypes.func,
};

BatchEditPayrollDialog.defaultProps = {
  onPayrollUpdated: null,
};

export default BatchEditPayrollDialog;
